import React from "react";
import style from "./style.module.css";

export type CheckboxSize = "small" | "large";
export type CheckboxTone = "light" | "dark";

type ForwardedInputAttrs = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  | "type"
  | "className"
  | "checked"
  | "disabled"
  | "onClick"
  | "onChange"
  | "size"
>;

export interface CheckboxProps extends ForwardedInputAttrs {
  className?: string;
  "data-testid"?: string;
  /** @deprecated kept as an escape hatch; prefer the `size`/`tone` props */
  checkboxClassName?: string;
  label?: React.ReactNode;
  disabled?: boolean;
  size?: CheckboxSize;
  tone?: CheckboxTone;
  isBoxRight?: boolean;
  isHovered?: boolean;
  isMinus?: boolean;
  checked?: boolean;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

const Checkbox = ({
  className,
  checkboxClassName,
  label = "",
  disabled = false,
  size = "small",
  tone = "light",
  isBoxRight = false,
  isHovered,
  isMinus = false,
  checked,
  "data-testid": dataTestId,
  ...props
}: CheckboxProps) => {
  const isControlledByChange = props.onChange !== undefined;

  const labelClassName = [
    style.container,
    size === "large" ? style.containerLarge : null,
    className
  ]
    .filter(Boolean)
    .join(" ");

  const checkmarkClassName = [
    style.checkmark,
    size === "large" ? style.large : style.small,
    tone === "dark" ? style.toneDark : null,
    checkboxClassName
  ]
    .filter(Boolean)
    .join(" ");

  const customLabelStyle: React.CSSProperties = {};
  const customCheckBoxStyle: React.CSSProperties = {};

  if (isBoxRight) {
    customLabelStyle.paddingLeft = "0px";
    customCheckBoxStyle.left = "auto";
    customCheckBoxStyle.right = 0;
  }

  return (
    <label
      className={labelClassName}
      data-disabled={disabled}
      style={customLabelStyle}
      data-testid="Checkbox"
    >
      {label}
      <input
        {...props}
        type="checkbox"
        name={typeof label === "string" ? label : undefined}
        readOnly={!isControlledByChange}
        data-testid={dataTestId || "checkbox"}
        checked={checked}
        disabled={disabled}
      />
      <span
        className={checkmarkClassName}
        data-is-hovered={isHovered}
        data-is-minus={isMinus}
        data-size={size}
        data-tone={tone}
        style={customCheckBoxStyle}
      />
    </label>
  );
};

export default Checkbox;
